<template>
  <b-container class="referral-jam-event-settings mb-5">
    <div class="d-flex justify-content-center align-items-center my-5" v-if="$apollo.loading">
      <b-spinner type="grow" variant="primary"/>
    </div>
    <template v-else-if="event && event.id">
      <div class="d-flex flex-row justify-content-start align-items-center my-2 border-bottom pb-2">
        <img class="event-logo mr-3" v-if="event.branding && event.branding.logo && event.branding.logo.fileLocation"
          :src="event.branding.logo.fileLocation"/>
        <h4 class="m-0">Referral Jam Settings</h4>
        <div class="flex-fill"/>
        <b-dropdown text="View" right variant="transparent">
          <b-dropdown-item :to="{ name: 'EventPage', params: { eventID: event.id } }" target="_blank">View Page</b-dropdown-item>
          <b-dropdown-item v-if="event && event.welcomeContent" :to="{ name: 'EventWelcome', params: { eventID: event.id } }" target="_blank">View Welcome Page</b-dropdown-item>
          <b-dropdown-item :to="{ name: 'EventRoom', params: { eventID: event.id } }" target="_blank">View Room</b-dropdown-item>
        </b-dropdown>
      </div>

      <b-alert show variant="warning" v-if="event.status === 'INACTIVE'">
        <div class="d-flex flex-row justify-content-center align-content-center">
          <p class="m-0"><strong>This event is disabled</strong> - enable this event to let participants sign up.</p>
          <div class="flex-fill"/>
          <b-btn class="m-0" @click.prevent="() => updateEvent({ status: 'ACTIVE' })" variant="link">Enable</b-btn>
        </div>
      </b-alert>

      <b-tabs card pills lazy :vertical="isWindowSM" v-model="currentTab" @activate-tab="tabChanged"
        nav-wrapper-class="bg-white" nav-class="bg-white">
          <b-tab title="Getting Started" v-if="isCreated">
            <h3>Getting Started</h3>
            <ol>
              <li>Configure your referral jam (e.g. start date &amp; end date)</li>
              <li>Edit pages for your referral jam (&amp; write some rules!)</li>
              <li>Add some prizes to your referral jam leaderboard</li>
              <li>Choose which jobs &amp; content you want to add</li>
              <li>Share the referral jam with your participants!</li>
            </ol>
          </b-tab>
          <b-tab title="General">
            <advanced-form autocomplete="off" layout="inline-md-3" submit-label="Save Event"
              v-bind="generalForm" v-model="generalValues"
              @update="() => hasChanges = true" @submit="updateGeneral"/>
          </b-tab>
          <b-tab title="Pages &amp; Rules">
            <h3>Page &amp; Rules</h3>
            <p>Write content for this referral jam.</p>
            <advanced-form autocomplete="off" layout="inline-md-3"  submit-label="Save Content"
              v-bind="contentForm" v-model="contentValues"
              @update="() => hasChanges = true" @submit="() => updateEvent(contentValues)"/>
          </b-tab>
          <b-tab title="Jobs &amp; Content">
            <h3>Jobs &amp; Content</h3>
            <p>Choose specific jobs &amp; content for this referral jam.</p>
            <advanced-form autocomplete="off" layout="inline-md-3"  submit-label="Save Related"
              v-bind="relationForm" v-model="relationValues"
              @update="() => hasChanges = true" @submit="updateRelations"/>
          </b-tab>
          <b-tab title="Branding &amp; Preferences">
            <h3>Branding &amp; Preferences</h3>
            <p>Set specific branding &amp; preferences for this referral jam.</p>
            <advanced-form v-bind="brandingForm" v-model="brandingValues" submit-label="Save Branding &amp; Preferences"
              @update="() => hasChanges = true" @submit="updateBranding"/>
          </b-tab>
          <b-tab title="Delete" title-link-class="text-danger bg-transparent" :active="toDelete">
            <h3 class="text-danger mb-3">Delete Referral Jam</h3>
            <p>If you delete this referral jam, <strong>all data &amp; references will also be deleted</strong>.<br/>
              This will not delete any jobs or content.</p>
            <p>This action cannot be undone - are you sure you wish to continue?</p>
            <b-btn @click.prevent="deleteEvent" variant="danger">Delete Referral Jam</b-btn>
          </b-tab>
      </b-tabs>
    </template>
  </b-container>
</template>

<script>
import gql from 'graphql-tag';
import AdvancedForm from '@src/components/AdvancedForm';
import Share from '@src/components/Share';
import gridMixin from '@src/mixins/grid';
import UpdateCollectiveGoal from '@/components/campaign-admin/update-collective-goal.vue';
import { AssetDetailsFragment, EventDetailsFragment } from '@src/graphql';
import { RelationJobFragment } from '@src/models/Jobs';
import { RelationContentSharesFragment } from '@/models/ContentShares';
import { createVueMetaBranding } from '@src/lib/branding';
import { flatten, unflatten } from 'flat';
import { getFontList } from '@src/lib/fonts';
import { mapRoot } from '@src/mixins/root';

const EventSettingsFragment = gql`
  fragment EventSettings on Event {
    ...EventDetails
    pageContent
    welcomeContent
    rulesContent

    participants {
      user {
        id
        fullName
      }
      createdAt
    }

    leaderboard {
      raffleMode

      prizes {
        first {
          title
          description
          image {
            ...AssetDetails
          }
          url
          terms
        }
        second {
          title
          description
          image {
            ...AssetDetails
          }
          url
          terms
        }
        third {
          title
          description
          image {
            ...AssetDetails
          }
          url
          terms
        }
        text
      }
      spotPrize {
        title
        description
        image {
          ...AssetDetails
        }
        url
        terms
      }

      scores {
        id
        icon
        label
        eventNames
        value
      }
    }

    collectiveGoal {
      description
      headline
      icon
      status
      units {
        icon
        name
        cap
        conversion
        showWholeYearTotal
        display {
          firstLine
          secondLine
        }
        monthlyGoal {
          text
          totalSum
        }
      }
    }

    jobs {
      ...RelationJobDetails
    }
    contentShares {
      ...RelationContentSharesDetails
    }
  }

  ${EventDetailsFragment}
  ${AssetDetailsFragment}
  ${RelationJobFragment}
  ${RelationContentSharesFragment}
`;

const GetEventSettingsQuery = gql`
  query GetEventSettings($eventID: ID!) {
    company {
      id
      event(where: { id: $eventID }) {
        ...EventSettings
      }
    }
  }
  ${EventSettingsFragment}
`;
const UpdateSettingsMutation = gql`
  mutation UpdateEventSettings($eventID: ID!, $update: UpdateEventInput!) {
    updateEvent(update: $update, where: { id: $eventID }) {
      ...EventSettings
    }
  }
  ${EventSettingsFragment}
`;

const ARE_YOU_SURE = 'There are outstanding changes - are you sure?';

export default {
  props: {
    eventID: {
      type: String,
      required: true,
    },
    campaignID: {
      type: String,
      required: true,
    },
    isCreated: {
      type: Boolean,
      default: false,
    },
    toDelete: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AdvancedForm,
    Share,
    UpdateCollectiveGoal,
  },
  mixins: [
    gridMixin,
  ],
  data: () => ({
    currentTab: null,
    hasChanges: false,
    generalValues: null,
    contentValues: null,
    leaderboardValues: null,
    collectiveGoalValues: null,
    relationValues: null,
    brandingValues: null,
  }),
  ...(process.env.VUE_ENV === 'client' ? {
    beforeRouteLeave(to, from, next) {
      if (process.env.VUE_ENV === 'client' && this.hasChanges && !confirm(ARE_YOU_SURE)) {
        return next(false);
      }

      next();
    },
  } : {}),
  metaInfo() {
    const head = {
      title: 'Referral Jam Settings',
    };

    if (this.event && this.event.id) {
      // Consistently set branding on this page too - enhances the preview!
      if (this.event.branding) {
        const isAdminUI = process.env.VUE_ENV === 'server' ? false : process.env.ADMIN_UI;
        const nonce = process.env.VUE_ENV === 'server' ? (this.$ssrContext.nonce || '') : '';
        createVueMetaBranding.call(this, head, this.event.branding, { isAdminUI, nonce });
      }
    }

    return head;
  },
  apollo: {
    event: {
      query: GetEventSettingsQuery,
      variables() {
        const { eventID } = this;
        return { eventID };
      },
      update(data) {
        const { company } = data || {};
        const { event } = company || {};
        return event;
      },
      result({ loading, data }) {
        if (!loading && data && data.company && data.company.event && data.company.event.id) {
          this.resetForms();
        }
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'company' ]),
    generalForm() {
      const isReferralJamSite = this.company && this.company.handle === 'real-links-events';

      const form = {
        ...(isReferralJamSite ? {
          'customFields.companyName': {
            label: 'Company Name',
            placeholder: 'Some Important Company',
            type: 'STRING',
            helpText: 'Optionally set the company name for this referral jam, used in emails, reports & more',
          },
        } : {}),
        title: {
          label: 'Title',
          placeholder: 'Referral Jam',
          required: true,
          type: 'STRING',
        },
        description: {
          label: 'Description',
          placeholder: 'e.g. Referral Jam',
          type: 'TEXTAREA',
        },
        startDate: {
          label: 'Start At',
          placeholder: 'e.g. 2020-12-01 13:00',
          helpText: 'The time to start this event, in your timezone',
          type: 'DATETIME',
        },
        endDate: {
          label: 'End At',
          placeholder: 'e.g. 2020-12-01 14:00',
          helpText: 'The time to end this event, in your timezone',
          type: 'DATETIME',
        },
        status: {
          label: 'Status',
          type: 'SELECT',
          required: true,
          choices: {
            ACTIVE: 'Active',
            INACTIVE: 'Disabled',
            DELETED: 'Deleted',
          },
        },
      };

      return { form };
    },
    contentForm() {
      const htmlExtensions = [
        'strong', 'italic', 'strike', 'underline',
        'link', 'ul', 'ol',
        'h1', 'h2', 'h3', 'h4',
        'img', 'iframe',
      ];

      const form = {
        pageContent: {
          label: 'Page Content',
          placeholder: 'Referral Jam',
          type: 'HTML',
          helpText: 'Enter page content',
          options: {
            extensions: htmlExtensions,
          },
        },
        welcomeContent: {
          label: 'Welcome Content',
          placeholder: 'Referral Jam',
          type: 'HTML',
          helpText: 'Enter welcome content - leave blank to disable the welcome page',
          options: {
            extensions: htmlExtensions,
          },
        },
        rulesContent: {
          label: 'Rules Content',
          placeholder: 'Referral Jam',
          type: 'HTML',
          helpText: 'Enter rules content',
          options: {
            extensions: htmlExtensions,
          },
        },
      };

      return { form };
    },
    leaderboardForm() {
      const form = {
        raffleMode: {
          label: 'Raffle Mode',
          type: 'CHECKBOX',
          description: 'Enable Raffle Mode 🎟',
          helpText: 'Raffle Mode allows participants to collect tickets to increase their chances of winning',
        },
        'prizes.text': {
          label: 'Prize Text',
          type: 'STRING',
          helpText: 'We take the first prize image & this text to promote the prizes for this event',
        },

        ...[ 'prizes.first', 'prizes.second', 'prizes.third', 'spotPrize' ].reduce((list, key) => ({
          ...list,
          [`${key}.title`]: {
            label: '— Title',
            type: 'STRING',
            helpText: 'Leave this empty to remove this prize',
            validation: {
              minLength: 3,
            },
          },
          [`${key}.image`]: {
            label: '— Image',
            type: 'FILE',
            helpText: 'Optionally include an image for the prize - for best results use a square image.',
            options: { type: 'IMAGE', accept: '.jpg,.jpeg,.gif,.png', showPreview: true },
          },
          [`${key}.description`]: {
            label: '— Description',
            type: 'TEXTAREA',
            helpText: 'Optionally write a description for this prize',
          },
          [`${key}.url`]: {
            label: '— URL',
            type: 'STRING',
            helpText: 'Optionally add a "Learn more" link to prizes',
            validation: {
              isURL: true,
            },
          },
          [`${key}.terms`]: {
            label: '— Terms',
            type: 'TEXTAREA',
            helpText: 'Optionally include any relevant Terms & Conditions',
          },
        }), {}),
      };

      return { form };
    },
    relationForm() {
      const { campaignID } = this;
      const form = {
        jobs: {
          label: 'Jobs',
          type: 'RELATION',
          options: {
            type: 'jobs',
            title: 'Jobs',
            many: true,
            where: {
              status: 'ACTIVE',
              campaigns: {
                id: campaignID
              }
            },
            order: 'CREATED_DESC',
          },
        },
        contentShares: {
          label: 'Content to Share',
          type: 'RELATION',
          options: {
            type: 'contentshares',
            title: 'Content Shares',
            many: true,
            where: {
              status: 'ACTIVE',
              campaigns: {
                id: campaignID
              }
            },
            order: 'CREATED_DESC',
          },
        },
      };

      return { form };
    },
    brandingForm() {
      const { handle, siteBranding: branding } = this.company;
      const { primaryColour, secondaryColour, backgroundColour } = branding || {};

      const isReferralJamSite = handle === 'real-links-events';

      const fontList = getFontList(handle).sort();

      const form = {
        ...(isReferralJamSite ? {
          'branding.logo': {
            label: 'Company Logo',
            placeholder: 'Select a logo',
            type: 'FILE',
            options: { type: 'IMAGE', accept: '.jpg,.jpeg,.gif,.png', showPreview: true },
            helpText: 'Optionally add a company logo to the top of this referral jam, left of the title',
          },
        } : {}),
        'branding.socialImage': {
          label: 'Social Image',
          placeholder: 'Select a social image',
          type: 'FILE',
          options: { type: 'IMAGE', accept: '.jpg,.jpeg,.gif,.png', showPreview: true },
          helpText: 'Displayed when sharing this event or jobs - for best results use a large landscape image',
        },
        ...(isReferralJamSite ? {
          'branding.headerFont': {
            label: 'Header Font',
            type: 'SELECT',
            placeholder: '— Default —',
            choices: fontList,
            helpText: 'Set the font for headers',
          },
          'branding.bodyFont': {
            label: 'Body Font',
            type: 'SELECT',
            placeholder: '— Default —',
            choices: fontList,
            helpText: 'Set the font for other text',
          },
          'branding.primaryColour': {
            label: 'Primary Colour',
            type: 'COLOR-PICKER',
            options: { defaultValue: primaryColour },
            helpText: 'Set the colour for buttons, links & other call-to-actions',
          },
          'branding.secondaryColour': {
            label: 'Secondary Colour',
            type: 'COLOR-PICKER',
            options: { defaultValue: secondaryColour },
            helpText: 'Set the colour for text & other items',
          },
          'branding.backgroundColour': {
            label: 'Background Colour',
            type: 'COLOR-PICKER',
            options: { defaultValue: backgroundColour },
            helpText: 'Set the background colour of the page',
          },
          'branding.customCSS': {
            label: 'Custom CSS',
            type: 'TEXTAREA',
            helpText: 'Add any additional custom CSS',
          },
          'branding.customJS': {
            label: 'Custom JS',
            type: 'TEXTAREA',
            helpText: 'Add any additional custom JS to the page',
          },
        } : {}),
        'preferences.applicationEmailAddresses': {
          label: 'Send Application Emails To',
          placeholder: 'someone@example.com, someone-else@example.com',
          helpText: 'Optionally send an email with candidate details on applications made from this Referral Jam to a set of recipients - separated with a comma',
          type: 'STRING',
        },
        // ...(isReferralJamSite ? {} : {
        //   'preferences.canSyncContacts': {
        //     type: 'CHECKBOX',
        //     description: 'Can employees sync contacts for this event?',
        //     options: { defaultValue: canSyncContacts },
        //     helpText: 'Optionally enable/disable contact syncing for this event, defaults to the global setting',
        //   },
        // }),
        'preferences.canScheduleLinkedIn': {
          description: 'Can schedule shares on LinkedIn',
          type: 'CHECKBOX',
        },
        'preferences.hideSaveToCalendar': {
          description: 'Hide Save To Calendar',
          helpText: 'Optionally hide the Save to Calendar block for this event',
          type: 'CHECKBOX',
        },
        'preferences.hideParticipantsList': {
          description: 'Hide Participants List',
          helpText: 'Optionally hide the Participants list block for this event',
          type: 'CHECKBOX',
        },
        'preferences.hideLinkedInSearch': {
          description: 'Hide LinkedIn Search',
          helpText: 'Optionally hide the LinkedIn search modal for jobs on this event',
          type: 'CHECKBOX',
        },
        'preferences.hideLeaderboard': {
          description: 'Hide Leaderboard (make sure collective goal is set up)',
          helpText: 'If this options is true the leaderbosrd won\'t be displayed on the Jam',
          type: 'CHECKBOX',
        }
        // 'preferences.hideReferIntroductionMessage': {
        //   description: 'Hide Refer Introduction message',
        //   helpText: 'Optionally hide the Referral Introduction prompt for this event',
        //   type: 'CHECKBOX',
        // },
      };

      return {
        autocomplete: 'off',
        layout: 'inline-md-3',
        form,
        submitLabel: 'Branding & Preferences',
      };
    },
    absoluteEventURL() {
      const { id, handle } = this.event;
      const { origin } = this.$root;
      return (handle || id) ? `${origin}/events/${handle || id}` : null;
    },
  },
  methods: {
    resetForms() {
      /* eslint-disable object-property-newline */
      const {
        title, description, startDate, endDate, preferences, status,
        pageContent, welcomeContent, rulesContent,
        branding, leaderboard, jobs, contentShares, collectiveGoal
      } = this.event || {};
      const { raffleMode, prizes, spotPrize } = leaderboard || {};
      const { hideSaveToCalendar, hideParticipantsList, hideReferIntroductionMessage } = preferences || {};

      const clearKeys = (obj, keys) => keys.forEach(key => delete obj[key]);

      this.generalValues = {
        title, description, startDate, endDate, status,
        hideSaveToCalendar, hideParticipantsList, hideReferIntroductionMessage,
      };
      this.contentValues = { pageContent, welcomeContent, rulesContent };

      this.leaderboardValues = {
        raffleMode,
        ...flatten({ prizes }, { maxDepth: 3 }),
        ...flatten({ spotPrize }, { maxDepth: 2 }),
      };
      clearKeys(this.leaderboardValues, [
        'prizes',
        'prizes.__typename',
        'prizes.first',
        'prizes.first.__typename',
        'prizes.second',
        'prizes.second.__typename',
        'prizes.third',
        'prizes.third.__typename',
        'spotPrize',
        'spotPrize.__typename',
      ]);

      this.collectiveGoalValues = { ...(collectiveGoal || {}) };
      console.log({ collectiveGoal: this.collectiveGoalValues });

      this.relationValues = { jobs, contentShares };

      this.brandingValues = flatten({
        branding: branding || {},
        preferences: preferences || {},
      }, { maxDepth: 2 });
      clearKeys(this.brandingValues, [
        'branding',
        'branding.__typename',
        'preferences',
        'preferences.__typename',
      ]);
    },
    tabChanged(next, prev, e) {
      if (next !== prev && prev >= 0 && this.hasChanges && confirm(ARE_YOU_SURE) === false) {
        e.preventDefault();
        return false;
      }

      this.hasChanges = false;
      this.resetForms();
      return true;
    },
    async updateEvent(update) {
      try {
        const { query, variables: createVariables } = this.$options.apollo.event;
        const variables = createVariables.call(this);

        this.$log.debug({ update });

        await this.$apollo.mutate({
          mutation: UpdateSettingsMutation,
          variables: { eventID: this.eventID, update },
          update(store, { data: { updateEvent: newEvent } }) {
            const data = store.readQuery({ query, variables });
            data.company.event = newEvent || data.company.event;
            store.writeQuery({ query, variables, data });
          },
        });

        this.resetForms();
        this.$notification({
          title: 'Success!',
          message: 'Updated event',
          variant: 'success',
        });
        this.hasChanges = false;
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err, { prepend: 'Failed to update the event:' });
      }
    },
    updateGeneral() {
      /* eslint-disable object-property-newline */
      const {
        title, description, startDate, endDate, status,
        hideSaveToCalendar, hideParticipantsList, hideReferIntroductionMessage,
      } = this.generalValues || {};

      return this.updateEvent({
        title, description, startDate, endDate, status,
        preferences: { hideSaveToCalendar, hideParticipantsList, hideReferIntroductionMessage },
      });
    },
    updateRelations() {
      const { jobs, contentShares } = this.relationValues || {};
      const update = {};

      if (Array.isArray(jobs) && jobs.length) {
        update.jobs = { id_in: jobs.map(({ id }) => id) };
      } else {
        update.jobs = null;
      }

      if (Array.isArray(contentShares) && contentShares.length) {
        update.contentShares = { id_in: contentShares.map(({ id }) => id) };
      } else {
        update.contentShares = null;
      }

      return this.updateEvent(update);
    },
    updateBranding() {
      const update = unflatten(this.brandingValues);

      if (update.branding) {
        [ 'logo', 'socialImage' ].forEach(key => {
          if (update.branding[key] && update.branding[key].id) {
            const { id: assetID } = update.branding[key];
            update.branding[key] = { id: assetID };
          }
        });
      }

      if (update.preferences) {
        if (update.preferences.applicationEmailAddresses) {
          update.preferences.applicationEmailAddresses = update.preferences.applicationEmailAddresses.split(',')
            .map(s => s.trim()).filter(s => s.includes('@')).join(', ') || null;
        }
      }

      return this.updateEvent(update);
    },
    async deleteEvent() {
      try {
        await this.$apollo.mutate({
          mutation: UpdateSettingsMutation,
          variables: { eventID: this.eventID, update: { status: 'DELETED' } },
        });

        this.$notification({
          title: 'Event deleted!',
          message: 'The event has been deleted.',
          variant: 'success',
        });
        this.$router.push({ name: 'EventList' });
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err, { prepend: 'Failed to delete the event:' });
      }
    },
  },
};
</script>

<style lang="scss">
.referral-jam-event-settings {
  .event-logo {
    max-height: 30px;
    max-width: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-referral-jam.vue